import React, { useContext } from "react";
import styled from "styled-components";
import { SubscriptionContext } from "../SubscriptionContext";

import {
    FormField,
    Toast,
    BusinessPortalHeader,
    PAYMENT_FLOW_MAX_WIDTH,
    PortalCaption,
    PortalMetricCard,
    PortalMetricLabel,
    PortalFormActions,
    PortalFormScrollBody,
    PortalFormSection,
    PortalFormSectionDescription,
    PortalFormSectionHeader,
    PortalFormSectionTitle,
    PortalFormSurface,
    PortalSectionTitle,
    StandardModalActionButton,
    StandardModalActionGroup,
    portalColors
} from "src/components";
import { Forms } from "../Subscription";

// assets
import LicenseIconAsset from "src/img/new/license.svg";

const LicenseNoteCard = styled(PortalMetricCard)`
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    align-content: center;
    gap: 0.9rem;
    min-height: auto;
`;

const LicenseIconImage = styled.img`
    width: 2.25rem;
    height: 2.25rem;
    object-fit: contain;
`;

const FieldWrap = styled.div`
    width: 100%;
    max-width: 17rem;
`;

const Plan = () => {
    const context = useContext(SubscriptionContext);
    const { currentForm, totalNumber, moreUsers, updatePrice, nextClick } = context;
    const isPlan = currentForm === Forms.PLAN;
    const licenseCount = isPlan ? totalNumber : moreUsers;

    const proceedToPayment = e => {
        e.preventDefault();

        const minimum = isPlan ? 5 : 1;
        const value = isPlan ? totalNumber : moreUsers;
        const units = minimum > 1 ? "users" : "user";

        if (!value || value < minimum) {
            return Toast.error("Minimum of " + minimum + " " + units + " to purchase.");
        }

        nextClick();
    };

    const handleUserToAddCount = (value) => {
        if (value > 0) {
            updatePrice(currentForm, value);
        }
    };

    return (
        <React.Fragment>
            <BusinessPortalHeader
                title={isPlan ? "User Licenses: Step 3" : "Purchase More User Licenses"}
                description={isPlan ?
                    "To start inviting your employees or clients to your Me Business app you must first purchase user licenses. These licenses will be used to send app invites and provided your users access to your custom business app."
                    : "Enter the amount of user license you require."
                }
                maxWidth={PAYMENT_FLOW_MAX_WIDTH}
            />
            <PortalFormSurface maxWidth={PAYMENT_FLOW_MAX_WIDTH} marginTop="0" flex="1 1 auto" minHeight="0">
                <PortalFormScrollBody columns="repeat(2, minmax(0, 1fr))" padding="1.25rem" gap="1rem">
                    <PortalFormSection>
                        <PortalFormSectionHeader withDivider>
                            <PortalFormSectionTitle>User Licenses</PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Enter Amount of User Licenses Required
                            </PortalFormSectionDescription>
                        </PortalFormSectionHeader>

                        <PortalCaption margin="0" maxWidth="31rem">
                            { isPlan ?
                                <React.Fragment>
                                    Please enter the total number of Users that will be invited to use
                                    your custom Me app. This includes employees and clients.
                                </React.Fragment>
                                :
                                "Specify the total number of user licenses you'd like to add to your subscription."
                            }
                        </PortalCaption>

                        <FieldWrap>
                            <FormField
                                margin="0"
                                height="3.125rem"
                                borderRadius="var(--calendar-radius-input, 8px)"
                                size="1.5625rem"
                                fontWeight="700"
                                placeholderWeight="700"
                                placeholderSize="1.5625rem"
                                value={licenseCount}
                                placeholder={licenseCount}
                                onChange={(e) => handleUserToAddCount(e.target.value)}
                                type="number"
                            />
                        </FieldWrap>

                        <PortalMetricLabel color={portalColors.mutedText}>
                            *Minimum of {isPlan ? 5 : 1} {isPlan ? "Licenses" : "License"}
                        </PortalMetricLabel>
                    </PortalFormSection>

                    <PortalFormSection>
                        <PortalFormSectionHeader withDivider>
                            <PortalFormSectionTitle>License Details</PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Review the number of user licenses before continuing to payment.
                            </PortalFormSectionDescription>
                        </PortalFormSectionHeader>

                        <PortalMetricCard minHeight="auto" padding="0.85rem">
                            <PortalMetricLabel>User Licenses</PortalMetricLabel>
                            <PortalSectionTitle size="1rem">{licenseCount}</PortalSectionTitle>
                        </PortalMetricCard>

                        {isPlan && (
                            <LicenseNoteCard minHeight="auto" padding="0.85rem">
                                <LicenseIconImage src={LicenseIconAsset} alt="" />
                                <div>
                                    <PortalSectionTitle size="0.95rem">Purchasing Additional User Licenses</PortalSectionTitle>
                                    <PortalCaption margin="0.25rem 0 0">
                                        You will be able to purchase additional licenses if needed once you purchase and complete your initial subscription.
                                    </PortalCaption>
                                </div>
                            </LicenseNoteCard>
                        )}
                    </PortalFormSection>
                </PortalFormScrollBody>

                <PortalFormActions sticky={false}>
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton
                            type="button"
                            onClick={() => context.backToPrevious()}
                        >
                            Back
                        </StandardModalActionButton>
                        <StandardModalActionButton
                            type="button"
                            $variant="primary"
                            onClick={(e) => proceedToPayment(e)}
                        >
                            Next
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                </PortalFormActions>
            </PortalFormSurface>
        </React.Fragment>
    );
};

export default Plan;
